import { useMemo } from 'react';
import * as Yup from 'yup';
import { FieldDefinition } from './useQuestionnaireFields';

/**
 * Hook to generate Yup validation schema based on field definitions
 */
export const useQuestionnaireValidation = (fields: FieldDefinition[]) =>
  useMemo(() => {
    const schemaFields: Record<string, Yup.AnySchema> = {};

    fields.forEach((field) => {
      if (field.required) {
        switch (field.type) {
          case 'text':
          case 'textarea':
            schemaFields[field.name] = Yup.string().required(`${field.label} is required`);
            break;

          case 'radio':
          case 'select':
            // For charge selection fields, validate the JSON structure
            if (field.domainType === 'SELECT_A_CHARGE_NAME') {
              schemaFields[field.name] = Yup.string()
                .required(`Charge Type is required`)
                .test('is-valid-charge', `Charge Type is required`, (value) => {
                  if (!value) return false;
                  try {
                    const parsed = JSON.parse(value);
                    return parsed && parsed.answerOptionId && parsed.answerText;
                  } catch {
                    return false;
                  }
                });
            } else {
              schemaFields[field.name] = Yup.string().required(`${field.label} is required`);
            }
            break;

          case 'multiselect':
            schemaFields[field.name] = Yup.array().min(1, `${field.label} is required`).required(`${field.label} is required`);
            break;

          case 'date':
          case 'datetime':
            schemaFields[field.name] = Yup.date().required(`${field.label} is required`);
            break;

          case 'daterange':
            schemaFields[field.name] = Yup.object({
              start: Yup.date().required('Start date is required'),
              end: Yup.date().required('End date is required'),
            }).required(`${field.label} is required`);
            break;

          default:
            schemaFields[field.name] = Yup.string().required(`${field.label} is required`);
            break;
        }
      } else {
        // Optional fields
        switch (field.type) {
          case 'text':
          case 'textarea':
          case 'radio':
          case 'select':
            schemaFields[field.name] = Yup.string().optional();
            break;

          case 'multiselect':
            schemaFields[field.name] = Yup.array().optional();
            break;

          case 'date':
          case 'datetime':
            schemaFields[field.name] = Yup.date().optional();
            break;

          case 'daterange':
            schemaFields[field.name] = Yup.object({
              start: Yup.date().optional(),
              end: Yup.date().optional(),
            }).optional();
            break;

          default:
            schemaFields[field.name] = Yup.string().optional();
            break;
        }
      }
    });

    return Yup.object(schemaFields);
  }, [fields]);
